import { useState } from 'react'
import css from './views.module.css'
import { Button } from '../ui'
import type { Block, Inline } from '../lib/markdown'

/**
 * The block renderer shared by every place this app shows markdown: the
 * runbook, which is prose we wrote, and a component's `description`, which is
 * prose the fleet published.
 *
 * It walks the typed nodes `lib/markdown.ts` produces and emits React
 * elements. There is no `dangerouslySetInnerHTML` here and there is none
 * anywhere in this app: there must be no path from document text to markup,
 * not even for content we wrote ourselves.
 *
 * The checklist handlers are optional because only the runbook has one. Given
 * none, a checklist renders as a list rather than as buttons that do nothing.
 */
export function Blocks({
  blocks,
  done,
  onToggle,
}: {
  blocks: Block[]
  done?: Record<string, boolean>
  onToggle?: (id: string) => void
}) {
  return (
    <>
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} done={done} onToggle={onToggle} />
      ))}
    </>
  )
}

function BlockView({
  block,
  done,
  onToggle,
}: {
  block: Block
  done?: Record<string, boolean>
  onToggle?: (id: string) => void
}) {
  switch (block.kind) {
    case 'heading': {
      if (block.level === 1) return <h1 className={css.mdH1}>{block.text}</h1>
      const Tag = block.level === 2 ? 'h2' : 'h3'
      return (
        <Tag id={block.id} className={block.level === 2 ? css.mdH2 : css.mdH3}>
          {block.text}
          <a className={css.anchor} href={`?s=${block.id}`} aria-label={`Link to ${block.text}`}>
            #
          </a>
        </Tag>
      )
    }
    case 'paragraph':
      return <p className={css.mdP}><Spans content={block.content} /></p>
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul'
      return (
        <Tag className={css.mdList}>
          {block.items.map((item, index) => (
            <li key={index}><Spans content={item} /></li>
          ))}
        </Tag>
      )
    }
    case 'checklist':
      return (
        <div className={css.mdCheck}>
          {block.items.map((item) =>
            onToggle ? (
              <button
                key={item.id}
                className={`${css.mdCheckRow} ${done?.[item.id] ? css.mdCheckDone : ''}`}
                onClick={() => onToggle(item.id)}
                aria-pressed={Boolean(done?.[item.id])}
              >
                <span className={css.mdCheckMark}>{done?.[item.id] ? '[x]' : '[ ]'}</span>
                <span><Spans content={item.content} /></span>
              </button>
            ) : (
              <div key={item.id} className={css.mdCheckRow}>
                <span className={css.mdCheckMark}>[ ]</span>
                <span><Spans content={item.content} /></span>
              </div>
            ),
          )}
        </div>
      )
    case 'code':
      return <CodeBlock text={block.text} />
    case 'table':
      return (
        <div className={css.mdTableWrap}>
          <table className={css.mdTable}>
            <thead>
              <tr>{block.head.map((cell, index) => <th key={index}>{cell}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}><Spans content={cell} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'quote':
      return <blockquote className={css.mdQuote}><Spans content={block.content} /></blockquote>
    case 'rule':
      return <hr className={css.mdRule} />
  }
}

/** A runbook is mostly commands. Copying one should not mean selecting it. */
function CodeBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className={css.mdCodeBlock}>
      <pre>{text}</pre>
      <div className={css.mdCodeCopy}>
        <Button
          variant="quiet"
          onClick={() => {
            navigator.clipboard.writeText(text).then(
              () => {
                setCopied(true)
                window.setTimeout(() => setCopied(false), 2000)
              },
              () => setCopied(false),
            )
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  )
}

export function Spans({ content }: { content: Inline[] }) {
  return (
    <>
      {content.map((span, index) => {
        switch (span.kind) {
          case 'code':
            return <code key={index} className={css.mdCodeInline}>{span.text}</code>
          case 'strong':
            return <strong key={index}><Spans content={span.content} /></strong>
          case 'em':
            return <em key={index}><Spans content={span.content} /></em>
          case 'link':
            return (
              <a key={index} href={span.href} target="_blank" rel="noreferrer">
                {span.text}
              </a>
            )
          case 'text':
            return <span key={index}>{span.text}</span>
        }
      })}
    </>
  )
}
